/**
 * EtfTransactions: editable table of Bitcoin ETF holdings and their
 * transactions, shown at the bottom of the Purchases page.
 *
 * For each holding: a section with the holding's symbol/name + a table
 * of its transactions (date, shares, price/share, category, note,
 * delete) plus an "add row" at the bottom.
 *
 * Per the user's spec: future buys will mostly be MSBT, so we lead
 * with that holding's add-row pre-selected. Categories match the BTC
 * purchase log enum plus a "starting_balance" for cost-unknown
 * initial entries.
 *
 * Patch I changes:
 *   - Section eyebrow / title renamed from "Retirement plan holdings"
 *     / "ETF positions" to "Bitcoin ETFs" / "Bitcoin ETF positions".
 *     The retirement-account framing was wrong: many users hold these
 *     in regular taxable accounts.
 *   - Description text dropped the "in retirement accounts" framing.
 *   - AddHoldingRow's submit handler now defensively checks that the
 *     store action is callable and surfaces errors via alert + console
 *     so the previous "click does nothing" failure mode produces a
 *     visible diagnostic instead of silent breakage.
 *   - Em-dashes scrubbed throughout.
 *
 * Patch AZ:
 *   - AddHoldingRow gets an autocomplete suggestion dropdown sourced
 *     from src/data/bitcoin-tickers.ts. As the user types in the
 *     symbol field, matching tickers + names appear; clicking one
 *     fills both fields and hides the dropdown. Free-form entry
 *     still works for symbols not in the curated list, so the
 *     autocomplete is purely additive. No new API calls: the list
 *     is bundled static data, and per-holding price polling already
 *     handles any symbol the user submits.
 *
 * Patch BA:
 *   - Delete-holding button in each per-holding header on the
 *     Purchases tab. confirm() prompt mentions the transaction count
 *     so the user sees what's about to be wiped, and notes the
 *     operation is undoable (the store wraps it in withUndo so the
 *     header Undo button can roll it back). removeEtfHolding cascades
 *     in the store: it filters out the holding AND every transaction
 *     pointing at its id in a single undo-able mutation, so there's
 *     no orphan-transaction risk.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useAppStore } from '../state/store.js';
import { Decimal, ZERO } from '../engine/money.js';
import { formatUsd } from './Money.js';
import { DecimalInput } from './DecimalInput.js';
import type { EtfHolding, EtfTransaction } from '../engine/types.js';
import { holdingPosition } from './EtfHoldings.js';
import { searchBitcoinTickers, type BitcoinTicker } from '../data/bitcoin-tickers.js';
import { AutoBuyPanel } from './AutoBuyPanel.js';
import { getCurrentSubdomain, isFIRETenant } from '../io/tenant.js';

const CATEGORY_LABELS: Record<EtfTransaction['category'], string> = {
  starting_balance: 'Starting balance',
  salary: isFIRETenant() ? 'Business income' : 'Salary',
  bonus: 'Bonus',
  tax_refund: 'Tax refund',
  stock_sale: 'Stock sale',
  profit_share: 'Profit share',
  loan: 'Loan',
  manual: 'Manual',
  other: 'Other',
};

/**
 * Categories users can pick when creating or editing transactions.
 * Excludes 'starting_balance' which is a legacy seed-only category.
 * The user asked to tag those as 'manual' instead. Existing rows with
 * starting_balance still render via CATEGORY_LABELS, but the dropdown
 * won't offer it as a new choice.
 */
const SELECTABLE_CATEGORIES: Array<EtfTransaction['category']> = [
  'manual', 'salary', 'bonus', 'tax_refund', 'stock_sale', 'profit_share', 'loan', 'other',
];

interface NewRowState {
  date: string;
  shares: string;
  pricePerShareUsd: string;
  category: EtfTransaction['category'];
  note: string;
}

function defaultNewRow(): NewRowState {
  return {
    date: new Date().toLocaleDateString('sv-SE'),
    shares: '',
    pricePerShareUsd: '',
    category: 'manual',
    note: '',
  };
}

export function EtfTransactions(): JSX.Element {
  const holdings = useAppStore(s => s.etfHoldings);
  const txs = useAppStore(s => s.etfTransactions);
  const addEtfTransaction = useAppStore(s => s.addEtfTransaction);
  const updateEtfTransaction = useAppStore(s => s.updateEtfTransaction);
  const removeEtfTransaction = useAppStore(s => s.removeEtfTransaction);
  const updateEtfHolding = useAppStore(s => s.updateEtfHolding);
  // Patch BA: removeEtfHolding cascades (drops the holding plus all
  // its transactions in one undoable step). The button wired up below
  // calls this after a confirm() that surfaces the transaction count.
  const removeEtfHolding = useAppStore(s => s.removeEtfHolding);

  // Per-holding new-row state.
  const [newRows, setNewRows] = useState<Record<string, NewRowState>>({});
  const getRow = (id: string): NewRowState => newRows[id] ?? defaultNewRow();
  const setRow = (id: string, patch: Partial<NewRowState>): void => {
    setNewRows(prev => ({ ...prev, [id]: { ...getRow(id), ...patch } }));
  };

  const addRow = (h: EtfHolding): void => {
    const r = getRow(h.id);
    if (!r.shares) return;
    addEtfTransaction({
      holdingId: h.id,
      date: new Date(r.date + 'T12:00:00Z'),
      shares: new Decimal(r.shares),
      pricePerShareUsd: r.pricePerShareUsd ? new Decimal(r.pricePerShareUsd) : undefined,
      category: r.category,
      note: r.note || undefined,
    });
    setRow(h.id, { shares: '', pricePerShareUsd: '', note: '' });
  };

  if (holdings.length === 0) {
    return (
      <section className="section">
        <p className="section-eyebrow">Bitcoin ETFs</p>
        <h2 className="section-title">Bitcoin ETF positions</h2>
        <p className="muted" style={{ fontSize: 'var(--fs-small)', marginBottom: 'var(--gap-m)' }}>
          No Bitcoin ETF holdings tracked yet. Add one below to get started.
        </p>
        <AddHoldingRow />
      </section>
    );
  }

  return (
    <section className="section">
      <p className="section-eyebrow">Bitcoin ETFs</p>
      <h2 className="section-title">Bitcoin ETF positions</h2>
      <p className="muted" style={{ fontSize: 'var(--fs-small)', marginBottom: 'var(--gap-m)' }}>
        Bitcoin ETF holdings. Add new buys below; P/L is tracked from entries
        with a cost basis.
      </p>

      {holdings.map(h => {
        const myTxs = txs
          .filter(t => t.holdingId === h.id)
          .sort((a, b) => a.date.getTime() - b.date.getTime());
        const pos = holdingPosition(h, txs);
        const newRow = getRow(h.id);

        return (
          <div key={h.id} style={{ marginBottom: 'var(--gap-l)' }}>
            <div className="row between" style={{ alignItems: 'baseline', marginBottom: 'var(--gap-s)' }}>
              <div>
                <span className="mono" style={{ fontWeight: 600 }}>{h.symbol}</span>
                <span className="muted" style={{ marginLeft: 8, fontSize: 'var(--fs-small)' }}>{h.name}</span>
              </div>
              {/* Patch BA: right-side cluster holds the shares+value
                  readout AND the Delete-holding button. Wrapped in a
                  flex container so the two sit on the same line without
                  fighting the parent's `row between` justification. */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gap-s)' }}>
                <span className="mono faint" style={{ fontSize: 'var(--fs-small)' }}>
                  {pos.currentShares.toFixed(0)} shares
                  {!pos.effectivePriceUsd.isZero() ? (
                    <> {'\u00B7'} ${formatUsd(pos.currentShares.times(pos.effectivePriceUsd), { decimals: 0 })}</>
                  ) : null}
                </span>
                {/* Patch BA: Delete button. Destructive action, so:
                    (1) confirm() before firing,
                    (2) message names the holding and the exact tx count
                        being wiped, so the user knows what they're losing,
                    (3) reminds them undo is available from the header.
                    The store action cascades to transactions. */}
                <button
                  className="ghost"
                  onClick={() => {
                    const txCount = myTxs.length;
                    const noun = txCount === 1 ? 'transaction' : 'transactions';
                    const msg = `Delete ${h.symbol} (${h.name})?\n\nThis removes the holding and ${txCount} ${noun}. You can undo this from the Undo button in the header.`;
                    if (confirm(msg)) {
                      removeEtfHolding(h.id);
                    }
                  }}
                  style={{ padding: '2px 8px', fontSize: 'var(--fs-micro)', color: 'var(--neg)' }}
                  title="Delete this holding and all its transactions (undoable)"
                >
                  delete
                </button>
              </div>
            </div>

            {/* Patch BB: per-holding auto-buy panel. When enabled, the
                walker creates new ETF transactions on scheduled dates
                priced at today's share price, flooring to whole shares
                (no fractional). Rows show an AUTO badge until edited. */}
            <AutoBuyPanel
              assetType="etf"
              assetId={h.id}
              assetLabel={`${h.symbol} ${h.name}`}
              assetHint="(no fractional shares; leftover dollars discard each period)"
            />

            {/* Aggregate P/L summary, visible only when the holding has cost
                basis on at least one transaction. As the user fills in cost
                on the starting-balance row, this lights up automatically.
                Computes:
                  total cost = sum(shares \u00D7 price/share) across all txs with price
                  market value = trackedShares \u00D7 current price
                  P/L = market value minus cost
                The widget on the dashboard shows the same numbers; this one
                lives next to the editable rows so the cause/effect of editing
                a price-per-share is visible without leaving the page. */}
            {(() => {
              if (!pos.trackedShares.greaterThan(0)) return null;
              const havePrice = !pos.effectivePriceUsd.isZero();
              const trackedValue = pos.trackedShares.times(pos.effectivePriceUsd);
              const pl = trackedValue.minus(pos.trackedCostUsd);
              const plPct = pos.trackedCostUsd.isZero() ? ZERO : pl.div(pos.trackedCostUsd).times(100);
              const plPos = pl.greaterThanOrEqualTo(0);
              return (
                <div className="row" style={{ alignItems: 'baseline', gap: 'var(--gap-m)', marginBottom: 'var(--gap-s)', flexWrap: 'wrap', fontSize: 'var(--fs-micro)' }}>
                  <span className="mono">
                    <span className="faint">cost: </span>
                    ${formatUsd(pos.trackedCostUsd, { decimals: 0 })}
                  </span>
                  {havePrice ? (
                    <>
                      <span className="mono">
                        <span className="faint">value: </span>
                        ${formatUsd(trackedValue, { decimals: 0 })}
                      </span>
                      <span className={`mono ${plPos ? 'delta pos' : 'delta neg'}`}>
                        {plPos ? '+' : ''}${formatUsd(pl, { decimals: 0 })}
                        {' '}({plPos ? '+' : ''}{plPct.toFixed(1)}%)
                      </span>
                    </>
                  ) : (
                    <span className="faint mono">value: - (no live price)</span>
                  )}
                </div>
              );
            })()}

            {/* Manual price override */}
            <div className="row" style={{ alignItems: 'center', gap: 8, marginBottom: 'var(--gap-s)', fontSize: 'var(--fs-micro)' }}>
              <span className="faint">Manual price override:</span>
              <DecimalInput
                step="0.01"
                decimals={2}
                placeholder={h.livePriceSnapshot
                  ? `live: $${h.livePriceSnapshot.priceUsd.toFixed(2)}`
                  : 'no live price'}
                value={h.manualPriceUsd}
                onChange={d => updateEtfHolding(h.id, { manualPriceUsd: d })}
                onClear={() => updateEtfHolding(h.id, { manualPriceUsd: undefined })}
                style={{ width: 100 }}
              />
              {h.manualPriceUsd ? (
                <button
                  className="ghost"
                  style={{ padding: '0 6px', fontSize: 'var(--fs-micro)' }}
                  onClick={() => updateEtfHolding(h.id, { manualPriceUsd: undefined })}
                  title="Clear manual override, use live"
                >
                  use live
                </button>
              ) : null}
            </div>

            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="num">Shares</th>
                  <th className="num">Price / share</th>
                  <th className="num">Cost</th>
                  <th>Category</th>
                  <th>Note</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {myTxs.map(t => {
                  const cost = t.pricePerShareUsd ? t.shares.times(t.pricePerShareUsd) : null;
                  return (
                    <tr key={t.id}>
                      <td>
                        {/* Patch BB: AUTO badge for walker-created rows.
                            updateEtfTransaction strips the autoCreated flag
                            on any patch, so editing any field demotes the
                            row to a regular manual entry. */}
                        {t.autoCreated ? (
                          <div style={{
                            display: 'inline-block',
                            fontSize: 9,
                            padding: '1px 5px',
                            marginBottom: 2,
                            background: 'var(--accent)',
                            color: 'var(--bg-deep)',
                            fontWeight: 700,
                            fontFamily: 'var(--mono)',
                            borderRadius: 2,
                            letterSpacing: '0.05em',
                          }} title="Created by auto-buy schedule. Editing any field on this row removes the tag.">
                            AUTO
                          </div>
                        ) : null}
                        <input
                          type="date"
                          className="left"
                          value={t.date.toISOString().slice(0, 10)}
                          onChange={e => updateEtfTransaction(t.id, { date: new Date(e.target.value + 'T12:00:00Z') })}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td className="num">
                        <DecimalInput
                          step="0.01"
                          decimals={2}
                          value={t.shares}
                          onChange={d => updateEtfTransaction(t.id, { shares: d })}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td className="num">
                        {/* Price-per-share is optional. Supports clearing back
                            to undefined for "no cost basis" state via onClear. */}
                        <DecimalInput
                          step="0.01"
                          decimals={2}
                          placeholder="0.00"
                          value={t.pricePerShareUsd}
                          onChange={d => updateEtfTransaction(t.id, { pricePerShareUsd: d })}
                          onClear={() => updateEtfTransaction(t.id, { pricePerShareUsd: undefined })}
                          style={{ width: '100%', minWidth: 80 }}
                          title="Cost per share at the time of purchase. Required for P/L tracking on this lot."
                        />
                      </td>
                      <td className="num dim">
                        {cost ? <>${formatUsd(cost, { decimals: 0 })}</> : '-'}
                      </td>
                      <td>
                        <select
                          value={t.category}
                          onChange={e => updateEtfTransaction(t.id, { category: e.target.value as EtfTransaction['category'] })}
                          style={{ width: '100%' }}
                        >
                          {/* If the row is still tagged with the legacy
                              starting_balance, show that as an option so the
                              select isn't out-of-sync with the value. Don't
                              offer it on rows that don't already use it. */}
                          {t.category === 'starting_balance' ? (
                            <option value="starting_balance">Starting balance</option>
                          ) : null}
                          {SELECTABLE_CATEGORIES.map(v => (
                            <option key={v} value={v}>{CATEGORY_LABELS[v]}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="left"
                          placeholder="-"
                          value={t.note ?? ''}
                          onChange={e => updateEtfTransaction(t.id, { note: e.target.value || undefined })}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <button
                          className="row-action"
                          title="Remove"
                          onClick={() => removeEtfTransaction(t.id)}
                        >
                          {'\u2715'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {/* Add row */}
                <tr>
                  <td>
                    <input type="date" className="left" value={newRow.date}
                      onChange={e => setRow(h.id, { date: e.target.value })} />
                  </td>
                  <td className="num">
                    <input type="number" step="0.01" placeholder="0" value={newRow.shares}
                      onChange={e => setRow(h.id, { shares: e.target.value })} />
                  </td>
                  <td className="num">
                    <input type="number" step="0.01" placeholder="0.00" value={newRow.pricePerShareUsd}
                      onChange={e => setRow(h.id, { pricePerShareUsd: e.target.value })} />
                  </td>
                  <td className="num dim">-</td>
                  <td>
                    <select
                      value={newRow.category}
                      onChange={e => setRow(h.id, { category: e.target.value as EtfTransaction['category'] })}
                      style={{ width: '100%' }}
                    >
                      {SELECTABLE_CATEGORIES.map(v => (
                        <option key={v} value={v}>{CATEGORY_LABELS[v]}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="left"
                      placeholder="optional"
                      value={newRow.note}
                      onChange={e => setRow(h.id, { note: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      className="primary"
                      onClick={() => addRow(h)}
                      disabled={!newRow.shares}
                      style={{ padding: '4px 10px', fontSize: 'var(--fs-micro)' }}
                    >
                      ADD
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Add a new holding. Useful when:
          - User deleted a holding by mistake (e.g. Grayscale BTC mini)
          - User adds a new ETF position they want to track
          A small inline form at the bottom rather than a modal so it
          reads as part of "edit your holdings" rather than a power-user
          feature. */}
      <AddHoldingRow />
    </section>
  );
}

/**
 * Inline form to add a new ETF holding. Symbol + display name; the
 * holding starts with no transactions, so the user adds those via the
 * per-holding "Add row" once it appears.
 *
 * Patch I: defensive submit handler. Pre-I, clicking the button when
 * the store action was missing or threw failed silently with no UI
 * feedback (the user saw "the button doesn't work"). Now we check that
 * the action is callable, wrap the call in try/catch, and surface any
 * error via alert plus console so failures are diagnosable.
 *
 * Patch AZ: autocomplete suggestion dropdown attached to the symbol
 * input. On focus the user sees the first N entries from the curated
 * BITCOIN_TICKERS list; typing filters by symbol prefix (primary) and
 * by name substring (secondary). Clicking a suggestion fills both
 * symbol and display name in one tap. Free-form typing still works
 * for symbols not in the list. The dropdown closes on click-outside
 * (mousedown listener pattern, same as ProspectLink in App.tsx; using
 * mousedown not blur so clicking a suggestion fires before the input
 * loses focus).
 */
function AddHoldingRow(): JSX.Element {
  const addEtfHolding = useAppStore(s => s.addEtfHolding);
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  // Patch AZ: dropdown visibility tracks focus on the symbol input.
  // Distinct from a generic "open" flag because the autocomplete only
  // belongs to one of the two inputs; the display-name field isn't
  // searchable.
  const [showDropdown, setShowDropdown] = useState(false);
  // Hovered suggestion symbol, used to tint the active row. Cheap
  // state-driven hover since inline-style :hover doesn't work in
  // React without a CSS class.
  const [hovered, setHovered] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Patch AZ: close-on-outside-click. mousedown (not click) so it
  // fires BEFORE the input's blur event would, which means clicking a
  // suggestion still gets processed even though it technically lives
  // outside the input. Same pattern as ProspectLink in App.tsx.
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const suggestions = useMemo(() => searchBitcoinTickers(symbol, 8), [symbol]);

  const submit = (): void => {
    const cleanSymbol = symbol.trim();
    if (!cleanSymbol) return;
    if (typeof addEtfHolding !== 'function') {
      console.error('[AddHoldingRow] store action addEtfHolding is not a function:', addEtfHolding);
      alert('Cannot add holding: store action is not available. Check the browser console.');
      return;
    }
    try {
      addEtfHolding({
        symbol: cleanSymbol.toUpperCase(),
        name: name.trim() || cleanSymbol.toUpperCase(),
      });
      setSymbol('');
      setName('');
      setShowDropdown(false);
    } catch (e) {
      console.error('[AddHoldingRow] addEtfHolding threw:', e);
      alert(`Could not add holding "${cleanSymbol}": ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Patch AZ: pick a suggestion, fill both fields, close the dropdown.
  // Doesn't auto-submit; the user might want to tweak the name first
  // (e.g. shortening "iShares Bitcoin Trust" to "iShares"). The Enter
  // key on the symbol input still submits, so power users can type +
  // arrow-down + enter once we add keyboard nav (not in this patch).
  const pickSuggestion = (t: BitcoinTicker): void => {
    setSymbol(t.symbol);
    setName(t.name);
    setShowDropdown(false);
  };

  return (
    <div ref={wrapRef} style={{ marginTop: 'var(--gap-l)', borderTop: '1px solid var(--rule)', paddingTop: 'var(--gap-m)' }}>
      <div className="row" style={{ gap: 'var(--gap-s)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span className="faint mono" style={{ fontSize: 'var(--fs-micro)', flexBasis: '100%' }}>
          Add a holding (start typing a ticker like <span className="mono">IBIT</span> or an issuer name like <span className="mono">fidelity</span>):
        </span>
        {/* Patch AZ: relative wrapper so the absolute-positioned
            suggestion dropdown anchors to the symbol input rather than
            to the page. */}
        <div style={{ position: 'relative', width: 140 }}>
          <input
            type="text"
            placeholder="Symbol (e.g. IBIT)"
            value={symbol}
            onChange={e => { setSymbol(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            style={{ width: '100%', fontFamily: 'var(--mono)' }}
          />
          {showDropdown && suggestions.length > 0 ? (
            <div
              role="listbox"
              style={{
                position: 'absolute',
                top: 'calc(100% + 2px)',
                left: 0,
                zIndex: 50,
                width: 340,
                maxHeight: 280,
                overflowY: 'auto',
                background: 'var(--bg-elev)',
                border: '1px solid var(--rule-strong)',
                borderRadius: 4,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.35)',
              }}
            >
              {suggestions.map(t => (
                <button
                  key={t.symbol}
                  type="button"
                  role="option"
                  onClick={() => pickSuggestion(t)}
                  onMouseEnter={() => setHovered(t.symbol)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '6px 10px',
                    background: hovered === t.symbol ? 'rgba(247, 147, 26, 0.10)' : 'transparent',
                    border: 0,
                    borderBottom: '1px solid var(--rule)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: 'var(--fs-small)',
                    color: 'inherit',
                  }}
                >
                  <span className="mono" style={{ fontWeight: 600 }}>{t.symbol}</span>
                  <span style={{ color: 'var(--text-soft)', marginLeft: 8, fontSize: 'var(--fs-micro)' }}>
                    {t.name}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <input
          type="text"
          placeholder="Display name (e.g. iShares Bitcoin Trust)"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          style={{ width: 280 }}
        />
        <button
          className="primary"
          onClick={submit}
          disabled={!symbol.trim()}
          style={{ padding: '4px 10px', fontSize: 'var(--fs-micro)' }}
        >
          + Add holding
        </button>
      </div>
    </div>
  );
}
